(ns ycweb.app.pos.delivery
  (:require
   [rum.core :as rum]
   [ycweb.components.forms :as forms]
   [ycweb.util.core :as u]
   [ycweb.components.grid :as grid]
   [ycweb.components.common :as cmn]
   [util.string :as str]
   [inflections.core :as in]
   [ycweb.app.pos.sell :as sell]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.slats :as slats]
   [ycweb.components.buttons :as buttons]
   [ycweb.util.comms :as comms]
   [ycweb.components.flow :as flow]
   [ycweb.components.transitions :as t]
   [clojure.set :as set]
   [ycweb.components.toaster :as toaster]))

(defonce delivery-lifecycle-listener
  (cmn/lifecycle-listener :delivery/customerlookup :setup
    (fn [app-atom]
      (cmn/debounce-in app-atom [:sell :delivery :lookup :email]
        200
        #(when (and (>= (count %) 4) (pos? (.indexOf % "@")) (pos? (.indexOf % ".")))
           (comms/send :pos/user-details (sell/sell-comms app-atom {:email %})))))))

(defn select-customer [delivery-atom]
  (swap! delivery-atom
    (fn [d]
      (let [lookup (:lookup d)]
        (->
          d
          (merge
            (->
              lookup
              (dissoc :uuid :postal)
              (set/rename-keys {:id :user_id :uuid :user_uuid}))
            {:receivername (u/glue " " (:firstname lookup) (:lastname lookup))
             :receivercell (:cell lookup)}))))))

(defn select-address [delivery-atom]
  (swap! delivery-atom
    (fn [d]
      (let [selected-address (:selected-address d)]
        (if selected-address
          (-> d
            (merge
              (->
                selected-address
                (dissoc :cell
                    ;; We've selected these on the previous flow, don't clobber (or even populate)
                  :company :vatnumber :postal :billingsuburb :billingcity :billingpostalcode)
                (set/rename-keys {:id :address_id :uuid :address_uuid})))
            (assoc :receivername (u/glue " " (:firstname selected-address) (:lastname selected-address)))
            (assoc :receivercell
              (if (str/blank? (:cell selected-address))
                (:tel selected-address) (:cell selected-address))))
          d)))))

(defn select-billing-address [delivery-atom]
  (swap! delivery-atom
    (fn [d]
      (let [selected-address (:selected-billing-address d)]
        (if selected-address
          (-> d
            (merge
              (->
                selected-address
                (select-keys [:id :uuid :address :suburb :city :postalcode :company :vatnumber])
                (set/rename-keys
                  {:id :billing_address_id
                   :uuid :billing_address_uuid
                   :address :postal
                   :suburb :billingsuburb
                   :city :billingcity
                   :postalcode :billingpostalcode}))))
          d)))))

(rum/defc delivery-summary-screen < rum/reactive
  [app-atom]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        cart-items (cmn/curact app-atom [:sell :cart :items])
        shop-items (sell/collate-items (filter (fn [i] (= (:delivery i) "Shop")) cart-items))
        delivery-items (sell/collate-items (filter (fn [i] (= (:delivery i) "Delivery")) cart-items))]
    (sell/sell-screen-wrapper app-atom :delivery/summary
      [[:div
        [:form.u-margin-top-24]
        [:div.section
         [:h2.u-text-center {:class (u/bem :heading [:standard])}
          "Confirm item fulfillment"]

         [:ul {:class (u/bem :order-calc [:fulfillment])}
          [:li.order-calc__item
           [:span.order-calc__item-icon-wrapper [:i {:class (u/bem :icon [:block :shopping-bag])}]]
           [:span.order-calc__item-name "Items to take now:"]
           [:span.order-calc__item-value [:b (reduce + (map :qty shop-items))]]]
          [:li.order-calc__item
           [:span.order-calc__item-icon-wrapper [:i {:class (u/bem :icon [:block :truck])}]]
           [:span.order-calc__item-name "Items to deliver:"]
           [:span.order-calc__item-value [:b (reduce + (map :qty delivery-items))]]]]]

        [:div {:class (u/bem :slats [:items-to-deliver])}
         [:h3.slat.heading-slat
          [:span.heading-slat__icon-wrapper
           [:i {:class (u/bem :icon [:block :truck])}]]
          [:span.heading-slat__text "For Delivery:"]]
         (map
           (fn [item]
             (slats/item-summary-slat item))
           delivery-items)]

        [:div.u-margin-bottom-24 {:class (u/bem :section [:medium])}
         (feedback/action-feedback :truck "Checked" " the delivery item list?" (rum/cursor-in delivery-atom [:summary-confirmed]) {})]]]

      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         (if (:summary-confirmed (cmn/curval delivery-atom))
           #(grid/dispatch-action app-atom :sell/checkout {}))}))))

(defmethod flow/render-screen :delivery/summary [app-atom screen]
  (delivery-summary-screen app-atom))

(rum/defc customer-screen < rum/reactive
  [app-atom progress-step
   {:keys [adding-from-cart? next-fn back-fn] :as screen-setup}]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        lookup-atom (rum/cursor-in app-atom [:sell :delivery :lookup])
        cart-atom (rum/cursor-in app-atom [:sell :cart])]
    (sell/sell-screen-wrapper app-atom progress-step
      [[:div
        [:form.u-margin-top-24
         [:div.section
          [:h2.u-text-center {:class (u/bem :heading [:standard])}
           "Customer Details"]
          (forms/input "Email Address:" "e.g. jane.smith@domain.com" (rum/cursor lookup-atom :email)
            {:type :email :auto-focus (cmn/auto-focus? app-atom) :show-email-helpers true})
          (forms/input "Cellphone Number:" "" (rum/cursor lookup-atom :cell)
            {:type :tel})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text (if adding-from-cart? "Save" "Next")
         :right-fn
         (if
           (and
             (->>
               (cmn/curact cart-atom [:items])
               (filter (fn [i] (= (:delivery i) "Delivery")))
               (empty?)
               not)
             (or
               (empty? (:email (cmn/curval lookup-atom)))
               (empty? (:cell (cmn/curval lookup-atom)))))
           nil

           #(do
              (select-customer delivery-atom)
              (sell/send-delivery-details app-atom)
              (t/transition-next app-atom
                (if adding-from-cart?
                  :delivery/addcustomeremail
                  :delivery/customeremail))))}))))

(defmethod flow/render-screen :delivery/addcustomer [app-atom screen]
  (customer-screen app-atom :scan
    {:adding-from-cart? true}))

(defmethod flow/barcode-dispatch-screen :delivery/addcustomer [_ app-atom scan]
  (toaster/open-subtle-progress app-atom)
  (comms/send :pos/user-details (sell/sell-comms app-atom {:scan scan})))

(defmethod flow/render-screen :delivery/customer [app-atom screen]
  (customer-screen app-atom :delivery/customer {}))

(defmethod flow/barcode-dispatch-screen :delivery/customer [_ app-atom scan]
  (toaster/open-subtle-progress app-atom)
  (comms/send :pos/user-details (sell/sell-comms app-atom {:scan scan})))

(defmethod comms/receive :pos/user-details [app-atom e]
  (toaster/close-subtle-progress app-atom)
  (let [loaded
        (assoc (:user e)
          :invoice true
          :newslettersubscribe false
          :promotionsubscribe false
          :addresslist (:addresses e)
          :selected-address (:selectedaddress e)
          :trade-user? (:trade-user? e))]

    (if
      (or
        (= (:email loaded) (get-in @app-atom [:sell :delivery :lookup :email]))
        (:scan e))
      (swap! app-atom
        (fn [state]
          (->
            state
            (assoc-in [:sell :delivery :lookup] loaded)))))))

(rum/defc customer-email-screen < rum/reactive
  [app-atom progress-step
   {:keys [adding-from-cart?]}]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery :lookup])
        newsletter-atom (rum/cursor delivery-atom :newslettersubscribe)
        promotions-atom (rum/cursor delivery-atom :promotionsubscribe)
        newsletter-selected (rum/react newsletter-atom)]
    (sell/sell-screen-wrapper app-atom progress-step
      [[:div
        [:form.u-margin-top-24
         [:div.section
          [:h2.u-text-center {:class (u/bem :heading [:standard])}
           "Customer Details"]
          [:div.form-field
           (forms/checkbox "Newsletters and personalised offers" newsletter-atom
             {:description "Find out first about new launches and deals, plus the chance to receive occasional vouchers"
              :change-fn
              #(swap! delivery-atom assoc
                 :promotionsubscribe @newsletter-atom
                 :newslettersubscribe @newsletter-atom)})]
          [:div.form-field
           (forms/checkbox "Personalised offers only" promotions-atom
             {:description "Sign up for the chance of occasional personalised vouchers, without getting our newsletters"
              :change-fn
              #(swap! delivery-atom assoc :promotionsubscribe @promotions-atom)
              :disabled newsletter-selected})]
          [:div.form-field
           (forms/checkbox "Receive confirmation email" (rum/cursor delivery-atom :invoice)
             {:description "We'll send you an email with your unique order number and a link to your online invoice."
              :default-checked true})]]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text (if adding-from-cart? "Save" "Next")
         :right-fn
         #(do
            (select-customer delivery-atom)
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom
              (if adding-from-cart?
                :delivery/addcompanydetails
                :delivery/companydetails)))}))))

(defmethod flow/render-screen :delivery/addcustomeremail [app-atom screen]
  (customer-email-screen app-atom :delivery/customer
    {:adding-from-cart? true}))

(defmethod flow/render-screen :delivery/customeremail [app-atom screen]
  (customer-email-screen app-atom :delivery/customer {}))

(rum/defc company-screen < rum/reactive
  [app-atom progress-step
   {:keys [adding-from-cart? next-fn next-address-fn] :as screen-setup}]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])]
    (sell/sell-screen-wrapper app-atom progress-step
      [[:div
        [:form.u-margin-top-24
         (into
           [:div.section
            [:h2.u-text-center {:class (u/bem :heading [:standard])}
             "Billing Details"]
            [:div.form-field
             (forms/radio [:b "No Billing/Company Details needed"]
               nil (rum/cursor delivery-atom :selected-billing-address))]

            [:div.form-field
             (forms/radio [:b "New Billing address for invoicing"]
               {} (rum/cursor delivery-atom :selected-billing-address))]]

           (map
             (fn [e]
               [:div.form-field
                (forms/radio
                  (u/glue " " (:firstname e) (:lastname e))
                  e (rum/cursor delivery-atom :selected-billing-address)
                  {:description
                   (u/glue ", " (:company e) (:address e) (:suburb e) (:city e) (:postalcode e))})])
             (rum/react (rum/cursor delivery-atom :addresslist))))]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text
         (if (= nil (cmn/curact delivery-atom [:selected-billing-address]))
           "Skip"
           (if adding-from-cart? "Save" "Next"))
         :right-fn
         (if (= nil (cmn/curact delivery-atom [:selected-billing-address]))
           #(do
              (sell/send-delivery-details app-atom)
              (t/transition-next app-atom
                (cond
                  adding-from-cart?
                  :sell

                  (sell/has-delivery-items? (sell/cart app-atom))
                  :delivery

                  :else
                  :delivery/cartnote)))
           #(do
              (select-billing-address delivery-atom)
              (sell/send-delivery-details app-atom)
              (t/transition-next app-atom
                (if adding-from-cart?
                  :delivery/addcompanyaddress
                  :delivery/companyaddress))))}))))

(defmethod flow/render-screen :delivery/addcompanydetails [app-atom screen]
  (company-screen app-atom :scan
    {:adding-from-cart? true}))

(defmethod flow/render-screen :delivery/companydetails [app-atom screen]
  (company-screen app-atom :delivery/company {}))

(rum/defc billing-address-screen
  [app-atom progress-step
   {:keys [adding-from-cart? next-fn] :as screen-setup}]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])]
    (sell/sell-screen-wrapper app-atom progress-step
      [[:div
        [:form.u-margin-top-24
         [:div.section
          (forms/input "Company:" ""
            (rum/cursor delivery-atom :company))
          (forms/input "VAT Number:" ""
            (rum/cursor delivery-atom :vatnumber))
          (forms/textarea "Billing Address:"
            (rum/cursor delivery-atom :postal))
          (forms/input "Suburb:" ""
            (rum/cursor delivery-atom :billingsuburb))
          (forms/input "City:" ""
            (rum/cursor delivery-atom :billingcity))
          (forms/input "Postal Code:" ""
            (rum/cursor delivery-atom :billingpostalcode)
            {:type :number :small-width true})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text (if adding-from-cart? "Save" "Next")
         :right-fn
         #(do
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom
              (cond
                adding-from-cart? :sell
                (sell/has-delivery-items? (sell/cart app-atom)) :delivery
                :else :delivery/cartnote)))}))))

(defmethod flow/render-screen :delivery/addcompanyaddress [app-atom screen]
  (billing-address-screen app-atom :scan
    {:adding-from-cart? true}))

(defmethod flow/render-screen :delivery/companyaddress [app-atom screen]
  (billing-address-screen app-atom :delivery/company {}))

(rum/defc method-screen < rum/reactive
  [app-atom]

  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        method-atom (rum/cursor-in app-atom [:sell :delivery :method])
        {:keys [partnerenabled threshold] :as delivery} (cmn/curact app-atom [:config :delivery])
        cart-items-atom (rum/cursor-in app-atom [:sell :cart :items])

        delivery-qty
        (->>
          (rum/react cart-items-atom)
          (filter #(= (:delivery %) "Delivery"))
          count)

        dropship-qty
        (->>
          (rum/react cart-items-atom)
          (filter
            #(or
               (= (:variation-delivery %) "dropship")
               (= (:variation-delivery %) "dropship_collect")))
          count)

        warehouselist-atom (rum/cursor app-atom :warehouse-list)
        warehouse-list
        (->>
          (rum/react warehouselist-atom)
          (filter :collectionenabled))]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          [:h2.u-text-center {:class (u/bem :heading [:standard])}
           "Choose Delivery Method"]
          (feedback/feedback :info ""
            (str (in/pluralize delivery-qty "item")
              (if (= delivery-qty 1) " is" " are")
              " marked for delivery."
              (when (pos? dropship-qty)
                " Dropship items can only be delivered by courier.")))

          [:div.form-field
           (forms/radio "Delivery to door" :door method-atom
             {:description (str "Free delivery for orders over R" threshold)})]
          (when-not (pos? dropship-qty)
            [:div.form-field
             (forms/radio "Collect from Yuppiechef" :collect method-atom {:disabled (empty? warehouse-list) :description (if (empty? warehouse-list) "In-store collections are temporarily suspended" "")})])

          (when-not (pos? dropship-qty)
            [:div.form-field
             (forms/radio "Collect from PostNet" :postnet method-atom {:disabled (not partnerenabled) :description (if partnerenabled (str "Free delivery for orders over R" threshold) "PostNet collections are temporarily suspended")})])]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         (if (rum/react method-atom)
           #(do
              (sell/send-delivery-details app-atom)
              (case (get-in @delivery-atom [:method])
                :door
                (if (empty? (get @delivery-atom :addresslist))
                  (t/transition-next app-atom :delivery/receiver-details)
                  (t/transition-next app-atom :delivery/addresslist))

                :collect (t/transition-next app-atom :delivery/store-select)
                :postnet (t/transition-next app-atom :delivery/postnet-select)
                (t/transition-next app-atom :delivery/giftmessage))))}))))

(defmethod flow/render-screen :delivery [app-atom screen]
  (method-screen app-atom))

(rum/defc store-screen < rum/reactive
  [app-atom]
  (let [store_id (cmn/curact app-atom [:login :store_id])
        warehouselist-atom (rum/cursor app-atom :warehouse-list)
        delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        [warehouse-list disabled-list]
        (->>
          (rum/react warehouselist-atom)
          (u/separate :collectionenabled))]
    (sell/sell-screen-wrapper app-atom :delivery
      [(when-not (empty? disabled-list)
         [:div.section
          (feedback/feedback :info ""
            (if (empty? warehouse-list)
              "Stores cannot fulfill colletions at this time, and have been temporarily disabled, please try a different collection method."
              "Collections are disabled at some stores and have been removed from this list temporarily."))])
       (when-not (empty? warehouse-list)
         [:div
          [:form.u-margin-top-24
           [:div.section
            (forms/select
              "Select Store:"
              (rum/cursor delivery-atom :store_id)
              (map
                (fn [{:keys [displayname store-id]}] {:value store-id :text displayname})
                warehouse-list)
              {:default store_id})]]])]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         #(do
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom :delivery/collection-receiver-details))}))))

(defmethod flow/render-screen :delivery/store-select [app-atom screen]
  (store-screen app-atom))

(defn normalize-city [city]
  (->> city
    str/trim
    u/capitalize-words))

(defmethod comms/receive :pos/postnet-reply [app-atom e]
  (swap! app-atom assoc :postnet-list (:postnet e)))

(defn postnet-lookup [app-atom]
  (when (empty? (:postnet-list @app-atom))
    (comms/send :pos/postnet-list (sell/sell-comms app-atom {})))
  (rum/cursor app-atom :postnet-list))

(rum/defc postnet-screen < rum/reactive
  [app-atom]
  (let [postnetlist-atom (postnet-lookup app-atom)
        delivery-atom (rum/cursor-in app-atom [:sell :delivery])

        postnetstore-atom (rum/cursor delivery-atom :postnet-storeid)
        postnetcity-atom (rum/cursor delivery-atom :postnet-city)
        postnetstore (u/parseInt (rum/react postnetstore-atom))
        postnetlist
        (->>
          (rum/react postnetlist-atom)
          (map
            (fn [store]
              (update store :city #(normalize-city %)))))

        grouped (group-by :city postnetlist)]

    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          (if (empty? (keys grouped))
            [:div.form-field.js-form-field
             (forms/field-label "City:" {})
             [:div.select-wrapper
              (forms/input-raw "Loading..." (atom nil) {:disabled true})
              [:i.select-icon]]]

            (forms/select
              "City:"
              postnetcity-atom
              (concat
                [{:value nil :text "Select a City"}]
                (map
                  (fn [k] {:value k :text k})
                  (sort (keys grouped))))
              {:default
               (if-not (zero? postnetstore)
                 (:city (first (filter #(= postnetstore (:id %)) postnetlist))))
               :change-fn
               #(reset! postnetstore-atom (:id (first (get grouped @postnetcity-atom))))}))

          (if (empty? (get grouped (rum/react postnetcity-atom)))
            [:div.form-field.js-form-field
             (forms/field-label "Branch:" {})
             [:div.select-wrapper
              (forms/input-raw "Select a city" (atom nil) {:disabled true})
              [:i.select-icon]]]

            (forms/select
              "Branch:" postnetstore-atom
              (concat
                [{:value nil :text "Select a Branch"}]
                (map
                  (fn [p] {:value (:id p) :text (:storename p)})
                  (concat
                    (sort-by :storename (get grouped (rum/react postnetcity-atom))))))
              {}))
          (when-let [selected (first (filter #(= (:id %) postnetstore) postnetlist))]
            [:p.blockquote.paragraph
             (:address1 selected)
             (when (u/presence (:address2 selected))
               [:span.u-block (:address2 selected)])

             (when (u/presence (:suburb selected))
               [:span.u-block (:suburb selected)])

             (when-not (= (:city selected) (:suburb selected))
               [:span.u-block (:city selected)])

             (str (:postcode selected))])]]]]
      []
      (grid/pos-call-to-action
        {:left-fn
         #(do
            (t/transition-prev app-atom)
            (reset! postnetlist-atom nil))
         :right-fn
         (when-not (zero? postnetstore)
           #(do
              (sell/send-delivery-details app-atom)
              (t/transition-next app-atom :delivery/postnet-receiver-details)
              (reset! postnetlist-atom nil)))}))))

(defmethod flow/render-screen :delivery/postnet-select [app-atom screen]
  (postnet-screen app-atom))

(rum/defc addresslist-screen < rum/reactive
  [app-atom]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        selected-address (rum/cursor delivery-atom :selected-address)]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         (into
           [:div.section
            [:div.form-field
             (forms/radio [:b "Delivery to new address"]
               nil selected-address)]]
           (map
             (fn [e]
               [:div.form-field
                (forms/radio
                  (u/glue " " (:firstname e) (:lastname e))
                  e selected-address
                  {:description
                   (u/glue ", " (:address e) (:suburb e) (:city e) (:postalcode e))})])
             (cmn/curact delivery-atom :addresslist)))]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         #(do
            (select-address delivery-atom)
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom :delivery/receiver-details))}))))

(defmethod flow/render-screen :delivery/addresslist [app-atom screen]
  (addresslist-screen app-atom))

(rum/defc receiver-screen
  [app-atom next-screen]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          (forms/input "Receiver's Name:" "e.g. John Smith"
            (rum/cursor delivery-atom :receivername)
            {:type :user})
          (forms/input "Receiver's Cellphone Number:" ""
            (rum/cursor delivery-atom :receivercell)
            {:type :tel})
          [:div.u-margin-top-24.u-text-right
           (buttons/button "Copy Customer Details"
             #(swap! delivery-atom
                (fn [s]
                  (assoc s
                    :receivername (u/glue " " (:firstname @delivery-atom) (:lastname @delivery-atom))
                    :receivercell (:cell @delivery-atom))))

             {:icon-left :copy-content :muted true :text-default true})
           (buttons/button "Clear"
             #(swap! delivery-atom
                (fn [s]
                  (assoc s
                    :receivername ""
                    :receivercell "")))
             {:icon-left :delete :muted true :text-default true})]]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         #(do
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom next-screen))}))))

(defmethod flow/render-screen :delivery/receiver-details [app-atom screen]
  (receiver-screen app-atom :delivery/address))

(defmethod flow/render-screen :delivery/collection-receiver-details [app-atom screen]
  (receiver-screen app-atom :delivery/giftmessage))

(defmethod flow/render-screen :delivery/postnet-receiver-details [app-atom screen]
  (receiver-screen app-atom :delivery/giftmessage))

(rum/defc address-screen
  [app-atom]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          ;; When we want to implement this search...
          #_[:div.card.form-field-card
             (forms/input "Search for Address:" ""
               (rum/cursor delivery-atom :google-search)
               {:type :location :hint "Powered by Google"})]

          (forms/textarea "Delivery Address:"
            (rum/cursor delivery-atom :address))
          (forms/input "Suburb:" ""
            (rum/cursor delivery-atom :suburb))
          (forms/input "City:" ""
            (rum/cursor delivery-atom :city))
          (forms/input "Postal Code:" ""
            (rum/cursor delivery-atom :postalcode)
            {:type :number :small-width true})
          [:p {:class (u/bem :form-label [:set])}
           "Address Type:"]

          [:div.form-field
           (forms/radio "Residential Address" "Residential" (rum/cursor delivery-atom :addresstype))]
          [:div.form-field
           (forms/radio "Business Address" "Business" (rum/cursor delivery-atom :addresstype))]]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         #(do
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom :delivery/instructions))}))))

(defmethod flow/render-screen :delivery/address [app-atom screen]
  (address-screen app-atom))

(rum/defc instructions-screen < rum/reactive
  [app-atom]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        instructions-atom (rum/cursor delivery-atom :specialinstructions)]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          (forms/textarea "Delivery Instructions: (Optional)"
            instructions-atom
            {:hint [:span "Please specify any special instructions for our delivery team."
                    [:br] [:b "Note:"]
                    " We deliver during business hours - we'll email a link to track the order."]})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text (if (str/blank? (rum/react instructions-atom)) "Skip" "Next")
         :right-fn
         #(do
            (sell/send-delivery-details app-atom)
            (t/transition-next app-atom :delivery/giftmessage))}))))

(defmethod flow/render-screen :delivery/instructions [app-atom screen]
  (instructions-screen app-atom))

(rum/defc giftmessage-screen < rum/reactive
  [app-atom]
  (let [delivery-atom (rum/cursor-in app-atom [:sell :delivery])
        message-atom (rum/cursor delivery-atom :giftmessage)
        gift-atom (rum/cursor delivery-atom :gift)
        message (rum/react message-atom)]
    (sell/sell-screen-wrapper app-atom :delivery
      [[:div
        [:form.u-margin-top-24
         [:div.section
          [:div.form-field
           (forms/checkbox "Include a Gift Message" gift-atom
             {:description "The order will arrive neatly packed with the message handwritten on a card."
              :change-fn #(reset! message-atom "")})]
          (forms/textarea "Gift Message:"
            message-atom
            {:limit 200 :hint (str (in/pluralize (- 200 (count message)) "Character") " remaining")
             :disabled (not (rum/react gift-atom))})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text (if (rum/react gift-atom) "Next" "Skip")
         :right-fn
         (if (and (rum/react gift-atom) (str/blank? message))
           nil
           #(do
              (sell/send-delivery-details app-atom)
              (t/transition-next app-atom :delivery/cartnote)))}))))

(defmethod flow/render-screen :delivery/giftmessage [app-atom screen]
  (giftmessage-screen app-atom))